import printJS from 'print-js';

/**
 * Returns true if the given image has been loaded and has a non-zero
 * image width. This will always report false on broken image links, or
 * otherwise malformed images that have no width.
 *
 * @param {HTMLImageElement} image an image whose loading state we'd like to know
 * @returns {boolean} true if the image is loaded
 */
const imageIsLoaded = image => image.naturalWidth > 0;

/**
 * Returns a promise that will resolve once all images in the given
 * container are loaded (viz. img.naturalWidth > 0), or the max wait time
 * has elapsed. The returned promise will never be rejected.
 *
 * Images are considered loaded once `img.naturalWidth > 0`. This means
 * that if you have zero-pixel or otherwise broken images, this function
 * must always wait the full timeout, so don't do that.
 *
 * @param {HTMLElement} imageContainer the DOM element that potentially contains
 *                                     images that need time to load.
 * @param {number} waitTimeMillis the maximum time in milliseconds to
 *                                wait for images in container to load.
 * @returns {Promise} a promise that is resolved once all images are loaded,
 *                    or the maximum wait time has elapsed
 */
const waitForImageLoadIn = (imageContainer, waitTimeMillis = 5000) =>
  new Promise(resolve => {
    const allImagesLoaded = () =>
      (!imageContainer
       || Array.from(imageContainer.querySelectorAll('img'))
               .every(imageIsLoaded));

    const waitUntilImagesLoaded = () => {
      if (allImagesLoaded() || waitTimeMillis-- <= 0) {
        return resolve();
      }
      setTimeout(waitUntilImagesLoaded, 1);
    };

    waitUntilImagesLoaded();
  });

/**
 * Finds the DOM element that the caller wants to print from the print JS config.
 *
 * @param {Object} printJSConfig print JS configuration as documented at http://printjs.crabbly.com/
 * @param {Function} getElementById a function to look up the printable element given
 *                   its DOM ID.
 * @returns {HTMLElement} the printable DOM element as looked up by getElementById.
 */
const findPrintableElement = (printJSConfig, getElementById = document.getElementById.bind(document)) => {
  const printableElementID =
    (typeof printJSConfig === 'string' ? printJSConfig
                                       : printJSConfig.printable);

  return getElementById(printableElementID);
};

/**
 * Wraps printJS to wait for images to load before attempting to print.
 *
 * See http://printjs.crabbly.com/ for documentation on the printJS API.
 *
 * @param {Object|string} printJSConfig the print JS configuration or printable object ID
 * @param {...any} rest other arguments to printJS
 *
 * @returns {Promise} that is resolved with the return value of printJS
 */
export default function print(printJSConfig, ...rest) {
  return waitForImageLoadIn(findPrintableElement(printJSConfig))
    .then(() => printJS(printJSConfig, ...rest)); // eslint-disable-line no-undef
}
